import React from 'react';
import { instanceOf } from 'prop-types';
import { Map } from 'immutable';
import moment from 'moment';
import { MdCheckCircle, MdError } from 'react-icons/md';

import InfoToolTip from '../../../components/InfoToolTip';

import STYLES from './DetailsComponent.module.scss';

const SetOptionsDetails = ({ operationDetails }) => (
  <div className={STYLES.DetailsComponent}>
    <h3>
      Operation Type{' '}
      <InfoToolTip
        title="Set Options"
        info="Sets options for an account, such as setting the inflation destination or adding an additional signer on an account. Allows you to set multiple options on an account in a single operation, such as changing an operation threshold and setting the flags on an account at the same time."
        placement="bottom"
        link="https://www.stellar.org/developers/guides/concepts/operations.html"
      />
    </h3>
    <div className={STYLES.mainDetails}>Set Options</div>
    <h3>
      Transaction Hash{' '}
      <InfoToolTip
        title="Transaction Hash"
        info="A hex-encoded SHA-256 hash of the transaction’s XDR-encoded form."
        placement="bottom"
        link="https://www.stellar.org/developers/guides/concepts/operations.html"
      />
    </h3>
    <div className={STYLES.keyBreakMain}>
      {operationDetails.get('transaction_hash')}
    </div>
    <div className={STYLES.otherDetails}>
      <div className={STYLES.keyValue}>
        <span className={STYLES.key}>Status</span>
        <InfoToolTip
          title="Status"
          info="Indicates if transaction was successful or not."
          placement="top"
          size="15px"
          link="https://www.stellar.org/developers/guides/concepts/transactions.html"
        />
        <span className={STYLES.value}>
          {operationDetails.get('transaction_successful') ? (
            <div className={STYLES.successLabel}>
              <MdCheckCircle className={STYLES.statusIcon} /> Successful
            </div>
          ) : (
            <div className={STYLES.failedLabel}>
              <MdError className={STYLES.statusIcon} /> Failed
            </div>
          )}
        </span>
      </div>
      {operationDetails && operationDetails.get('created_at') && (
        <div className={STYLES.keyValue}>
          <span className={STYLES.key}>Created at</span>
          <InfoToolTip
            title="Created at"
            info="The timestamp of when the operation was created."
            placement="top"
            size="15px"
            link="https://www.stellar.org/developers/guides/concepts/operations.html"
          />
          <span className={STYLES.value}>
            {moment(operationDetails.get('created_at')).format(
              'MMM DD YYYY, h:mm a'
            )}
          </span>
        </div>
      )}
      {operationDetails && operationDetails.get('signer_key') && (
        <div className={STYLES.keyValue}>
          <span className={STYLES.key}>Signer Key</span>
          <InfoToolTip
            title="Signer Key"
            info="The public key of the new signer."
            placement="top"
            size="15px"
            link="https://www.stellar.org/developers/guides/concepts/operations.html"
          />
          <span className={STYLES.keyBreakValue}>
            {operationDetails.get('signer_key')}
          </span>
        </div>
      )}
      {operationDetails && operationDetails.get('signer_weight') && (
        <div className={STYLES.keyValue}>
          <span className={STYLES.key}>Signer Key Weight</span>
          <InfoToolTip
            title="Signer Key Weight"
            info="The weight of the new signer"
            placement="top"
            size="15px"
            link="https://www.stellar.org/developers/guides/concepts/operations.html"
          />
          <span className={STYLES.value}>
            {operationDetails.get('signer_weight')}
          </span>
        </div>
      )}
      {operationDetails && operationDetails.get('master_key_weight') && (
        <div className={STYLES.keyValue}>
          <span className={STYLES.key}>Master Key Weight</span>
          <InfoToolTip
            title="Master Key Weight"
            info="The weight of the master key"
            placement="top"
            size="15px"
            link="https://www.stellar.org/developers/guides/concepts/operations.html"
          />
          <span className={STYLES.value}>
            {operationDetails.get('master_key_weight')}
          </span>
        </div>
      )}
      {operationDetails && operationDetails.get('home_domain') && (
        <div className={STYLES.keyValue}>
          <span className={STYLES.key}>Home Domain</span>
          <InfoToolTip
            title="Home Domain"
            info="The home domain used for reverse federation lookup"
            placement="top"
            size="15px"
            link="https://www.stellar.org/developers/guides/concepts/operations.html"
          />
          <span className={STYLES.value}>
            {operationDetails.get('home_domain')}
          </span>
        </div>
      )}
      {operationDetails && operationDetails.get('set_flags') && (
        <div className={STYLES.keyValue}>
          <span className={STYLES.key}>Numeric Value Flags Set</span>
          <InfoToolTip
            title="Numeric Value Flags Set"
            info="The array of numeric values of flags that has been set in this operation"
            placement="top"
            size="15px"
            link="https://www.stellar.org/developers/guides/concepts/operations.html"
          />
          <span className={STYLES.value}>
            {operationDetails.get('set_flags').map((flag, i) => (
              <span key={i}>{flag}, </span>
            ))}
          </span>
        </div>
      )}
      {operationDetails && operationDetails.get('set_flags_s') && (
        <div className={STYLES.keyValue}>
          <span className={STYLES.key}>String Value Flags Set</span>
          <InfoToolTip
            title="String Value Flags Set"
            info="The array of string values of flags that has been set in this operation"
            placement="top"
            size="15px"
            link="https://www.stellar.org/developers/guides/concepts/operations.html"
          />
          <span className={STYLES.value}>
            {operationDetails.get('set_flags_s').map((flag, i) => (
              <span key={i}>{flag}, </span>
            ))}
          </span>
        </div>
      )}
      {operationDetails && operationDetails.get('clear_flags') && (
        <div className={STYLES.keyValue}>
          <span className={STYLES.key}>Numeric Value Flags Cleared</span>
          <InfoToolTip
            title="Numeric Value Flags Cleared"
            info="The array of numeric values of flags that has been cleared in this operation"
            placement="top"
            size="15px"
            link="https://www.stellar.org/developers/guides/concepts/operations.html"
          />
          <span className={STYLES.value}>
            {operationDetails.get('clear_flags').map((flag, i) => (
              <span key={i}>{flag}, </span>
            ))}
          </span>
        </div>
      )}
      {operationDetails && operationDetails.get('clear_flags_s') && (
        <div className={STYLES.keyValue}>
          <span className={STYLES.key}>String Value Flags Cleared</span>
          <InfoToolTip
            title="String Value Flags Cleared"
            info="The array of string values of flags that has been cleared in this operation"
            placement="top"
            size="15px"
            link="https://www.stellar.org/developers/guides/concepts/operations.html"
          />
          <span className={STYLES.value}>
            {operationDetails.get('clear_flags_s').map((flag, i) => (
              <span key={i}>{flag}, </span>
            ))}
          </span>
        </div>
      )}
    </div>
  </div>
);

SetOptionsDetails.propTypes = {
  operationDetails: instanceOf(Map),
};

SetOptionsDetails.defaultProps = {
  operationDetails: new Map(),
};

export default SetOptionsDetails;
